import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  onSnapshot,
  addDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { getHolesByCourse, getStrokesForHole, getBergvlietTeeboxes } from './courseData';
import { NOTIFICATION_TYPES, getGlobalNotificationSettings } from './notifications';

const TEEBOX_COLORS = {
  wit: '#ffffff',
  geel: '#d4af37',
  blauw: '#3b82f6',
  rood: '#ef4444',
  oranje: '#f97316',
};

function AdminPanel({ onClose, userProfile, currentUserId }) {
  const [view, setView] = useState('seasons');
  const [seasons, setSeasons] = useState([]);
  const [events, setEvents] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSeasonForm, setShowNewSeasonForm] = useState(false);
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [showBulkEventForm, setShowBulkEventForm] = useState(false);
  const [showTrophyForm, setShowTrophyForm] = useState(false);
  
  // Trophy form state
  const [selectedUser, setSelectedUser] = useState('');
  const [trophyType, setTrophyType] = useState('season_champion');
  const [trophyYear, setTrophyYear] = useState(new Date().getFullYear());
  const [trophyNote, setTrophyNote] = useState('');
  const [customTrophyName, setCustomTrophyName] = useState('');
  const [customTrophyIcon, setCustomTrophyIcon] = useState('🏆');
  const [existingTrophies, setExistingTrophies] = useState([]);
const [allUsersMap, setAllUsersMap] = useState({});

  // Form states
  const [newSeasonName, setNewSeasonName] = useState('');
  const [newSeasonStartDate, setNewSeasonStartDate] = useState('');
  const [newSeasonEndDate, setNewSeasonEndDate] = useState('');

  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventSeason, setNewEventSeason] = useState('');
  const [newEventType, setNewEventType] = useState('regular');

  // Season Finale (18-hole) config
  const [finaleTeebox, setFinaleTeebox] = useState('admin');
  const [finaleAdminTeebox, setFinaleAdminTeebox] = useState('geel');
  const [bonusStrokes, setBonusStrokes] = useState({ 1: 10, 2: 8, 3: 6, 4: 4, 5: 2 });
  const [bonusMinRounds, setBonusMinRounds] = useState(10);

  const [bulkEventBaseName, setBulkEventBaseName] = useState('');
  const [bulkEventCount, setBulkEventCount] = useState(10);
  const [bulkEventStartDate, setBulkEventStartDate] = useState('');
  const [bulkEventSeason, setBulkEventSeason] = useState('');

  // Season Recap state
  const [recapLoading, setRecapLoading] = useState(false);
  const [recapPreview, setRecapPreview] = useState(null);   // computed data before publish
  const [publishedRecap, setPublishedRecap] = useState(null); // data from Firestore if published
  const [recapPublishing, setRecapPublishing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, emoji, confirmLabel, confirmColor, onConfirm }
  const [alertModal, setAlertModal] = useState(null); // { title, message, emoji, type: 'success' | 'error' | 'info' }
  const [promptModal, setPromptModal] = useState(null); // { title, message, emoji, placeholder, confirmLabel, onConfirm(value) }
  const [promptValue, setPromptValue] = useState('');

  // Season Finale winner reveal state
  const [finaleEvents, setFinaleEvents] = useState([]);
  const [finaleLoading, setFinaleLoading] = useState(false);
  const [finaleRounds, setFinaleRounds] = useState([]);
  const [revealLoading, setRevealLoading] = useState(false);
  const [finaleNonParticipants, setFinaleNonParticipants] = useState([]);
  const [savingNonParticipants, setSavingNonParticipants] = useState(false);
  const [nonParticipantsSaved, setNonParticipantsSaved] = useState(false);

  // Global notification settings (admin-wide kill switch per type)
  const [notificationSettings, setNotificationSettings] = useState({});
  const [notificationSettingsLoading, setNotificationSettingsLoading] = useState(false);
  const [clearingNotifications, setClearingNotifications] = useState(false);

  const showAlert = (title, message, type = 'info') => {
    const emoji = type === 'success' ? '✅' : type === 'error' ? '⚠️' : 'ℹ️';
    setAlertModal({ title, message, type, emoji });
  };

  const showPrompt = ({ title, message, emoji = '✏️', placeholder = '', confirmLabel = 'Save', initialValue = '', onConfirm }) => {
    setPromptValue(initialValue);
    setPromptModal({ title, message, emoji, placeholder, confirmLabel, onConfirm });
  };

  const canManageMembers = ['admin', 'moderator', 'officer'].includes(userProfile?.role);
  const canManageSeasons = ['admin', 'moderator'].includes(userProfile?.role);

  useEffect(() => {
    loadSeasons();
    loadEvents();
    if (canManageMembers) {
      loadPendingMembers();
      loadAllMembers();
    }
  }, [canManageMembers]);

  const loadSeasons = async () => {
    try {
      const seasonsSnapshot = await getDocs(collection(db, 'seasons'));
      const seasonsData = seasonsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      seasonsData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setSeasons(seasonsData);
    } catch (error) {
      console.error('Error loading seasons:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      eventsData.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadPendingMembers = () => {
    const q = query(collection(db, 'users'), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const members = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setPendingMembers(members);
    });
    return unsubscribe;
  };

  const loadAllMembers = () => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const members = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      members.sort((a, b) => a.name.localeCompare(b.name));
      setAllMembers(members);
      setLoading(false);
    });
    return unsubscribe;
  };

  const handleApproveMember = async (uid) => {
    try {
      await updateDoc(doc(db, 'users', uid), {
        status: 'approved'
      });
      showAlert('Member Approved', 'The membership request has been approved.', 'success');
    } catch (error) {
      console.error('Error approving member:', error);
      showAlert('Error', 'Error approving member', 'error');
    }
  };

  const handleDenyMember = (uid) => {
    setConfirmModal({
      title: 'Deny Membership',
      message: 'Are you sure you want to deny this membership request?',
      emoji: '🚫',
      confirmLabel: 'Deny',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await updateDoc(doc(db, 'users', uid), { status: 'denied' });
          showAlert('Member Denied', 'The membership request has been denied.', 'success');
        } catch (error) {
          console.error('Error denying member:', error);
          showAlert('Error', 'Error denying member', 'error');
        }
      },
    });
  };

  const handleRevokeMembership = (uid) => {
    setConfirmModal({
      title: 'Revoke Membership',
      message: 'Are you sure you want to revoke this membership? This will set their status to denied.',
      emoji: '🚫',
      confirmLabel: 'Revoke',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await updateDoc(doc(db, 'users', uid), { status: 'denied' });
          showAlert('Membership Revoked', 'This member’s membership has been revoked.', 'success');
        } catch (error) {
          console.error('Error revoking membership:', error);
          showAlert('Error', 'Error revoking membership', 'error');
        }
      },
    });
  };

  const handlePromoteToOfficer = (uid, currentRole) => {
    const isModerator = currentRole === 'moderator';
    showPrompt({
      title: isModerator ? 'Update Moderator Title' : 'Promote to Officer',
      message: isModerator
        ? 'Enter moderator title (e.g., Secretary, Treasurer):'
        : 'Enter officer title (e.g., Secretary, Treasurer):',
      emoji: isModerator ? '✏️' : '🎖️',
      placeholder: 'e.g., Secretary',
      confirmLabel: isModerator ? 'Update' : 'Promote',
      onConfirm: async (officerTitle) => {
        if (!officerTitle) return;
        setPromptModal(null);

        const updates = { officerTitle };
        if (!isModerator) {
          updates.role = 'officer';
        }

        try {
          await updateDoc(doc(db, 'users', uid), updates);
          showAlert('Success', isModerator ? 'Moderator title updated!' : 'User promoted to officer!', 'success');
        } catch (error) {
          console.error('Error updating user:', error);
          showAlert('Error', 'Error updating user', 'error');
        }
      },
    });
  };

  const handleDemoteFromOfficer = (uid) => {
    setConfirmModal({
      title: 'Remove Officer',
      message: 'Remove officer privileges and title?',
      emoji: '🎖️',
      confirmLabel: 'Remove',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await updateDoc(doc(db, 'users', uid), {
            role: 'member',
            officerTitle: null
          });
          showAlert('Success', 'User demoted to member', 'success');
        } catch (error) {
          console.error('Error demoting user:', error);
          showAlert('Error', 'Error demoting user', 'error');
        }
      },
    });
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    
    try {
      const activeSeasonsQuery = query(collection(db, 'seasons'), where('isActive', '==', true));
      const activeSeasons = await getDocs(activeSeasonsQuery);
      
      for (const seasonDoc of activeSeasons.docs) {
        await updateDoc(doc(db, 'seasons', seasonDoc.id), {
          isActive: false
        });
      }

      await addDoc(collection(db, 'seasons'), {
        name: newSeasonName,
        startDate: newSeasonStartDate,
        endDate: newSeasonEndDate,
        isActive: true,
        createdAt: serverTimestamp()
      });

      showAlert('Season Created', 'Season created successfully!', 'success');
      setNewSeasonName('');
      setNewSeasonStartDate('');
      setNewSeasonEndDate('');
      setShowNewSeasonForm(false);
      loadSeasons();
    } catch (error) {
      console.error('Error creating season:', error);
      showAlert('Error', 'Error creating season', 'error');
    }
  };

  const handleSetActiveSeason = (seasonId) => {
    setConfirmModal({
      title: 'Set Active Season',
      message: 'Set this as the active season? This will deactivate all other seasons.',
      emoji: '📅',
      confirmLabel: 'Set Active',
      confirmColor: '#10b981',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const seasonsSnapshot = await getDocs(collection(db, 'seasons'));
          for (const seasonDoc of seasonsSnapshot.docs) {
            await updateDoc(doc(db, 'seasons', seasonDoc.id), {
              isActive: seasonDoc.id === seasonId
            });
          }
          showAlert('Success', 'Active season updated!', 'success');
          loadSeasons();
        } catch (error) {
          console.error('Error setting active season:', error);
          showAlert('Error', 'Error setting active season', 'error');
        }
      },
    });
  };

  const handleDeleteSeason = (seasonId) => {
    setConfirmModal({
      title: 'Delete Season',
      message: 'Delete this season? This cannot be undone!',
      emoji: '🗑️',
      confirmLabel: 'Delete',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await deleteDoc(doc(db, 'seasons', seasonId));
          showAlert('Season Deleted', 'Season deleted', 'success');
          loadSeasons();
        } catch (error) {
          console.error('Error deleting season:', error);
          showAlert('Error', 'Error deleting season', 'error');
        }
      },
    });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    try {
      const eventData = {
        name: newEventName,
        date: newEventDate,
        seasonId: newEventSeason,
        type: newEventType,
        createdAt: serverTimestamp()
      };

      if (newEventType === 'finale_18') {
        // Lock in bonus stroke assignments now, based on regular season standings
        // as of the finale's creation date — so they don't shift later as players
        // complete their finale rounds on different days.
        const bonusAssignments = {};
        try {
          const roundsQuery = query(
            collection(db, 'rounds'),
            where('seasonId', '==', newEventSeason),
            where('status', '==', 'approved')
          );
          const roundsSnapshot = await getDocs(roundsQuery);
          const rounds = roundsSnapshot.docs.map(d => d.data()).filter(r => !r.isFinale);

          const userStats = {};
          rounds.forEach(round => {
            const uid = round.userId;
            if (!userStats[uid]) {
              userStats[uid] = { totalPoints: 0, roundsPlayed: 0, allScores: [] };
            }
            userStats[uid].totalPoints += round.totalStableford || 0;
            userStats[uid].roundsPlayed += 1;
            userStats[uid].allScores.push(round.totalStableford || 0);
          });

          const qualifying = Object.entries(userStats)
            .filter(([, stats]) => stats.roundsPlayed >= bonusMinRounds)
            .map(([uid, stats]) => {
              let dropCount = 0;
              if (stats.roundsPlayed >= 25) dropCount = 4;
              else if (stats.roundsPlayed >= 20) dropCount = 3;
              else if (stats.roundsPlayed >= 15) dropCount = 2;
              else if (stats.roundsPlayed >= 10) dropCount = 1;

              let adjustedTotal = stats.totalPoints;
              let countingRounds = stats.roundsPlayed;
              if (dropCount > 0) {
                const sorted = [...stats.allScores].sort((a, b) => a - b);
                adjustedTotal -= sorted.slice(0, dropCount).reduce((s, v) => s + v, 0);
                countingRounds -= dropCount;
              }
              return {
                userId: uid,
                averagePoints: countingRounds > 0 ? adjustedTotal / countingRounds : 0
              };
            });

          qualifying.sort((a, b) => b.averagePoints - a.averagePoints);
          qualifying.slice(0, 5).forEach((p, i) => {
            const rank = i + 1;
            bonusAssignments[p.userId] = bonusStrokes[rank] || 0;
          });
        } catch (bonusError) {
          console.error('Error locking in finale bonus assignments:', bonusError);
        }

        eventData.finaleConfig = {
          teeboxMode: finaleTeebox,
          adminTeebox: finaleAdminTeebox,
          bonusStrokes: bonusStrokes,
          bonusMinRounds: bonusMinRounds,
          bonusAssignments: bonusAssignments,
        };
      }

      const docRef = await addDoc(collection(db, 'events'), eventData);

      try {
        const globalSettings = await getGlobalNotificationSettings();
        if (globalSettings.new_event !== false) {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const batch = writeBatch(db);
          usersSnapshot.docs.forEach((userDoc) => {
            if (userDoc.data()?.notificationPrefs?.newEvent === false) return;
            const notifRef = doc(collection(db, 'notifications'));
            batch.set(notifRef, {
              userId: userDoc.id,
              type: 'new_event',
              title: 'New Event Posted',
              message: `${newEventName} - ${new Date(newEventDate).toLocaleDateString()} 🏌️`,
              icon: '🏌️',
              read: false,
              timestamp: serverTimestamp(),
              metadata: { eventId: docRef.id }
            });
          });
          await batch.commit();
        }
      } catch (notifError) {
        console.error('Error sending event notifications:', notifError);
      }

      showAlert('Event Created', 'Event created successfully!', 'success');
      setNewEventName('');
      setNewEventDate('');
      setNewEventSeason('');
      setNewEventType('regular');
      setFinaleTeebox('admin');
      setFinaleAdminTeebox('geel');
      setBonusStrokes({ 1: 10, 2: 8, 3: 6, 4: 4, 5: 2 });
      setBonusMinRounds(10);
      setShowNewEventForm(false);
      loadEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      showAlert('Error', 'Error creating event', 'error');
    }
  };

  const handleBulkCreateEvents = (e) => {
    e.preventDefault();

    setConfirmModal({
      title: 'Bulk Create Events',
      message: `Create ${bulkEventCount} events starting from ${bulkEventBaseName} 1?`,
      emoji: '📆',
      confirmLabel: 'Create',
      confirmColor: '#10b981',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const startDate = new Date(bulkEventStartDate);

          for (let i = 1; i <= bulkEventCount; i++) {
            const eventDate = new Date(startDate);
            eventDate.setDate(startDate.getDate() + ((i - 1) * 7));

            await addDoc(collection(db, 'events'), {
              name: `${bulkEventBaseName} ${i}`,
              date: eventDate.toISOString().split('T')[0],
              seasonId: bulkEventSeason,
              type: 'regular',
              createdAt: serverTimestamp()
            });
          }

          showAlert('Events Created', `${bulkEventCount} events created successfully!`, 'success');
          setBulkEventBaseName('');
          setBulkEventCount(10);
          setBulkEventStartDate('');
          setBulkEventSeason('');
          setShowBulkEventForm(false);
          loadEvents();
        } catch (error) {
          console.error('Error creating bulk events:', error);
          showAlert('Error', 'Error creating bulk events', 'error');
        }
      },
    });
  };

  const handleDeleteEvent = (eventId) => {
    setConfirmModal({
      title: 'Delete Event',
      message: 'Delete this event? This cannot be undone!',
      emoji: '🗑️',
      confirmLabel: 'Delete',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await deleteDoc(doc(db, 'events', eventId));
          showAlert('Event Deleted', 'Event deleted', 'success');
          loadEvents();
        } catch (error) {
          console.error('Error deleting event:', error);
          showAlert('Error', 'Error deleting event', 'error');
        }
      },
    });
  };

  const handleAwardTrophy = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      showAlert('Missing Information', 'Please select a user', 'error');
      return;
    }

    try {
      const trophyData = {
        userId: selectedUser,
        awardedBy: currentUserId,
        awardedByName: userProfile.name,
        year: parseInt(trophyYear),
        note: trophyNote,
        createdAt: serverTimestamp()
      };

      if (trophyType === 'custom') {
        trophyData.type = 'custom';
        trophyData.name = customTrophyName;
        trophyData.icon = customTrophyIcon;
      } else {
        trophyData.type = trophyType;
        
        // Set standard trophy names and icons
        const trophyMap = {
          'season_champion': { name: 'Season Champion', icon: '🏆' },
          'finale_winner': { name: 'Finale Winner', icon: '⭐' },
          'hall_of_fame': { name: 'Hall of Fame', icon: '👑' },
          'most_improved': { name: 'Most Improved', icon: '🔥' },
          'lifetime_achievement': { name: 'Lifetime Achievement', icon: '💎' }
        };
        
        trophyData.name = trophyMap[trophyType].name;
        trophyData.icon = trophyMap[trophyType].icon;
      }

      await addDoc(collection(db, 'special_trophies'), trophyData);

      showAlert('Trophy Awarded', 'Trophy awarded successfully!', 'success');
      setSelectedUser('');
      setTrophyType('season_champion');
      setTrophyYear(new Date().getFullYear());
      setTrophyNote('');
      setCustomTrophyName('');
      setCustomTrophyIcon('🏆');
      setShowTrophyForm(false);
    } catch (error) {
      console.error('Error awarding trophy:', error);
      showAlert('Error', 'Error awarding trophy', 'error');
    }
  };
  useEffect(() => {
  const loadTrophies = async () => {
    try {
      const trophiesSnapshot = await getDocs(collection(db, 'special_trophies'));
      const trophies = trophiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      trophies.sort((a, b) => b.year - a.year);
      setExistingTrophies(trophies);
    } catch (error) {
      console.error('Error loading trophies:', error);
    }
  };

  const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
    const usersMap = {};
    snapshot.docs.forEach(doc => {
      usersMap[doc.id] = doc.data();
    });
    setAllUsersMap(usersMap);
  });

  loadTrophies();
  return unsubscribe;
}, []);

const handleDeleteTrophy = (trophyId) => {
  setConfirmModal({
    title: 'Delete Trophy',
    message: 'Delete this trophy? This cannot be undone!',
    emoji: '🗑️',
    confirmLabel: 'Delete',
    confirmColor: '#ef4444',
    onConfirm: async () => {
      setConfirmModal(null);
      try {
        await deleteDoc(doc(db, 'special_trophies', trophyId));
        setExistingTrophies(prev => prev.filter(t => t.id !== trophyId));
        showAlert('Trophy Deleted', 'Trophy deleted!', 'success');
      } catch (error) {
        console.error('Error deleting trophy:', error);
        showAlert('Error', 'Error deleting trophy', 'error');
      }
    },
  });
};

  const getSeasonName = (seasonId) => {
    const season = seasons.find(s => s.id === seasonId);
    return season ? season.name : 'Unknown Season';
  };

  // ─── Season Recap helpers ────────────────────────────────────────────────

  const computeStdDev = (values) => {
    if (values.length < 2) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  };

  const computeRecapData = (rounds, usersMap, finalePositions = {}, finaleParticipants = new Set()) => {
    // Finale rounds (18-hole season finale) never count toward regular season awards
    const regularRounds = rounds.filter(r => !r.isFinale);

    // Group rounds by user and sort chronologically by approvedAt
    const byUser = {};
    regularRounds.forEach(r => {
      if (!byUser[r.userId]) byUser[r.userId] = [];
      byUser[r.userId].push(r);
    });
    Object.values(byUser).forEach(rds =>
      rds.sort((a, b) => (a.approvedAt || '').localeCompare(b.approvedAt || ''))
    );

    // seasonLeaderboardWinner: highest average stableford points per round (matches leaderboard)
    let seasonLeaderboardWinner = null;
    Object.entries(byUser).forEach(([uid, rds]) => {
      const total = rds.reduce((s, r) => s + (r.totalStableford || 0), 0);
      const avg = Math.round(total / rds.length);
      if (!seasonLeaderboardWinner || avg > seasonLeaderboardWinner.totalPoints) {
        seasonLeaderboardWinner = { userId: uid, name: usersMap[uid]?.name || 'Unknown', totalPoints: avg };
      }
    });

    // mostImproved: first half avg vs second half avg, min 10 rounds
    let mostImproved = null;
    Object.entries(byUser).forEach(([uid, rds]) => {
      if (rds.length < 10) return;

      // Sort chronologically by approvedAt
      const sorted = [...rds].sort((a, b) => (a.approvedAt || '').localeCompare(b.approvedAt || ''));

      // Split: first half rounds down, second half rounds up (11 rounds -> first 5, last 6)
      const total = sorted.length;
      const firstHalfCount = Math.floor(total / 2);
      const secondHalfCount = total - firstHalfCount;

      const firstHalf = sorted.slice(0, firstHalfCount);
      const secondHalf = sorted.slice(firstHalfCount);

      const firstAvg = firstHalf.reduce((s, r) => s + (r.totalStableford || 0), 0) / firstHalfCount;
      const secondAvg = secondHalf.reduce((s, r) => s + (r.totalStableford || 0), 0) / secondHalfCount;

      const improvement = parseFloat((secondAvg - firstAvg).toFixed(1));

      if (!mostImproved || improvement > mostImproved.improvement) {
        mostImproved = {
          userId: uid,
          name: usersMap[uid]?.name || 'Unknown',
          improvement,
          firstAvg: parseFloat(firstAvg.toFixed(1)),
          secondAvg: parseFloat(secondAvg.toFixed(1)),
          roundsPlayed: total,
          firstHalfCount,
          secondHalfCount,
        };
      }
    });

    // mostGrossBirdies: gross birdie = strokes === par - 1.
    // Same counter as the "Most Birdies" Season Record on the dashboard (App.js),
    // so the recap and the dashboard always agree at the moment of generation.
    const birdieCountMap = {};
    const birdieRoundsMap = {};
    regularRounds.forEach(r => {
      const uid = r.userId;
      if (birdieCountMap[uid] == null) { birdieCountMap[uid] = 0; birdieRoundsMap[uid] = 0; }
      birdieRoundsMap[uid]++;
      if (!r.holeData) return;
      try {
        const courseHoles = getHolesByCourse(r.course);
        Object.entries(r.holeData).forEach(([holeNum, hd]) => {
          if (!hd.strokes) return;
          const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
          if (!holeInfo) return;
          if (hd.strokes === holeInfo.par - 1) birdieCountMap[uid]++;
        });
      } catch (_) {}
    });
    let mostGrossBirdies = null;
    Object.entries(birdieCountMap).forEach(([uid, count]) => {
      if (!mostGrossBirdies || count > mostGrossBirdies.count) {
        const roundsPlayed = birdieRoundsMap[uid] || 0;
        mostGrossBirdies = {
          userId: uid,
          name: usersMap[uid]?.name || 'Unknown',
          count,
          roundsPlayed,
          perRound: roundsPlayed > 0 ? parseFloat((count / roundsPlayed).toFixed(2)) : 0,
        };
      }
    });

    // bestSingleRound: highest totalStableford in a single round
    let bestSingleRound = null;
    regularRounds.forEach(r => {
      const pts = r.totalStableford || 0;
      if (!bestSingleRound || pts > bestSingleRound.points) {
        bestSingleRound = {
          userId: r.userId,
          name: usersMap[r.userId]?.name || 'Unknown',
          points: pts,
          eventName: r.eventName || '',
        };
      }
    });

    // ironMan: most rounds played — ties share the award
    let maxRounds = 0;
    Object.values(byUser).forEach(rds => {
      if (rds.length > maxRounds) maxRounds = rds.length;
    });

    const ironManWinners = [];
    Object.entries(byUser).forEach(([uid, rds]) => {
      if (rds.length === maxRounds) {
        ironManWinners.push({
          userId: uid,
          name: usersMap[uid]?.name || 'Unknown',
          roundsPlayed: rds.length,
        });
      }
    });

    const ironMan = ironManWinners.length === 0
      ? null
      : ironManWinners.length === 1
        ? ironManWinners[0]
        : {
            userId: ironManWinners.map(p => p.userId).join(','),
            name: ironManWinners.map(p => p.name).join(' & '),
            roundsPlayed: maxRounds,
            isMultiple: true,
            winners: ironManWinners,
          };

    // mostConsistent: most rounds scored at 16+ stableford points.
    // Ties broken by the higher share of rounds at 16+, then by lower stdDev.
    const CONSISTENT_THRESHOLD = 16;
    let mostConsistent = null;
    Object.entries(byUser).forEach(([uid, rds]) => {
      const scores = rds.map(r => r.totalStableford || 0);
      const count = scores.filter(v => v >= CONSISTENT_THRESHOLD).length;
      if (count === 0) return;

      const roundsPlayed = rds.length;
      const pct = Math.round((count / roundsPlayed) * 100);
      const avg = parseFloat((scores.reduce((a, b) => a + b, 0) / roundsPlayed).toFixed(1));
      const stdDev = parseFloat(computeStdDev(scores).toFixed(1));

      const better =
        !mostConsistent ||
        count > mostConsistent.count ||
        (count === mostConsistent.count && pct > mostConsistent.pct) ||
        (count === mostConsistent.count && pct === mostConsistent.pct && stdDev < mostConsistent.stdDev);

      if (better) {
        mostConsistent = {
          userId: uid,
          name: usersMap[uid]?.name || 'Unknown',
          count,
          roundsPlayed,
          pct,
          threshold: CONSISTENT_THRESHOLD,
          avg,
          stdDev,
          lowestRound: Math.min(...scores),
        };
      }
    });

    // mostBeers
    const beerTotals = {};
    regularRounds.forEach(r => {
      beerTotals[r.userId] = (beerTotals[r.userId] || 0) + (r.beersAfterRound || 0);
    });
    let mostBeers = null;
    Object.entries(beerTotals).forEach(([uid, count]) => {
      if (!mostBeers || count > mostBeers.count) {
        mostBeers = { userId: uid, name: usersMap[uid]?.name || 'Unknown', count };
      }
    });

    // mostCigarettes
    const cigTotals = {};
    regularRounds.forEach(r => {
      cigTotals[r.userId] = (cigTotals[r.userId] || 0) + (r.cigarettesSmoked || 0) + (r.cigarettesBorrowed || 0);
    });
    let mostCigarettes = null;
    Object.entries(cigTotals).forEach(([uid, count]) => {
      if (!mostCigarettes || count > mostCigarettes.count) {
        mostCigarettes = { userId: uid, name: usersMap[uid]?.name || 'Unknown', count };
      }
    });

    // playerStats
    const totals = Object.entries(byUser)
      .map(([uid, rds]) => ({ uid, total: rds.reduce((s, r) => s + (r.totalStableford || 0), 0) }))
      .sort((a, b) => b.total - a.total);
    const positions = {};
    totals.forEach((p, i) => { positions[p.uid] = i + 1; });

    const playerStats = {};
    Object.entries(byUser).forEach(([uid, rds]) => {
      const scores = rds.map(r => r.totalStableford || 0);
      const total = scores.reduce((s, v) => s + v, 0);

      // Favorite course
      const courseCounts = {};
      rds.forEach(r => { if (r.course) courseCounts[r.course] = (courseCounts[r.course] || 0) + 1; });
      const favCourse = Object.entries(courseCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

      // GIR percentage (from pre-calculated field on rounds, 9 holes per round)
      let totalGIR = 0, girRounds = 0;
      rds.forEach(r => { if (r.greensInRegulation != null) { totalGIR += r.greensInRegulation; girRounds++; } });
      const girPct = girRounds > 0 ? Math.round((totalGIR / (girRounds * 9)) * 100) : null;

      // Score distribution (net vs par per hole)
      const dist = { eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0, triple: 0 };
      rds.forEach(r => {
        if (!r.holeData || !r.course) return;
        try {
          const courseHoles = getHolesByCourse(r.course);
          Object.entries(r.holeData).forEach(([holeNum, hd]) => {
            if (!hd.strokes) return;
            const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
            if (!holeInfo) return;
            const received = getStrokesForHole(holeInfo.strokeIndex, r.courseHandicap || 0, r.course);
            const diff = (hd.strokes - received) - holeInfo.par;
            if (diff <= -2) dist.eagle++;
            else if (diff === -1) dist.birdie++;
            else if (diff === 0) dist.par++;
            else if (diff === 1) dist.bogey++;
            else if (diff === 2) dist.double++;
            else dist.triple++;
          });
        } catch (_) {}
      });

      // Par type averages (gross strokes)
      const parStats = { 3: { total: 0, count: 0 }, 4: { total: 0, count: 0 }, 5: { total: 0, count: 0 } };
      rds.forEach(r => {
        if (!r.holeData || !r.course) return;
        try {
          const courseHoles = getHolesByCourse(r.course);
          Object.entries(r.holeData).forEach(([holeNum, hd]) => {
            if (!hd.strokes) return;
            const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
            if (!holeInfo) return;
            const p = holeInfo.par;
            if (parStats[p]) { parStats[p].total += hd.strokes; parStats[p].count++; }
          });
        } catch (_) {}
      });
      const par3avg = parStats[3].count > 0 ? parseFloat((parStats[3].total / parStats[3].count).toFixed(1)) : null;
      const par4avg = parStats[4].count > 0 ? parseFloat((parStats[4].total / parStats[4].count).toFixed(1)) : null;
      const par5avg = parStats[5].count > 0 ? parseFloat((parStats[5].total / parStats[5].count).toFixed(1)) : null;

      // Social totals
      const totalBeers = rds.reduce((s, r) => s + (r.beersAfterRound || 0), 0);
      const totalCigs  = rds.reduce((s, r) => s + (r.cigarettesSmoked || 0) + (r.cigarettesBorrowed || 0), 0);

      playerStats[uid] = {
        name: usersMap[uid]?.name || 'Unknown',
        // Season finale position (lowest net gross = #1).
        // null if the player did not play any holes in the finale.
        finalPosition: finaleParticipants.has(uid) ? (finalePositions[uid] ?? null) : null,
        roundsPlayed: rds.length,
        totalPoints: total,
        avgPoints: rds.length > 0 ? parseFloat((total / rds.length).toFixed(1)) : 0,
        bestRound: scores.length > 0 ? Math.max(...scores) : 0,
        favoriteCourse: favCourse,
        girPct,
        scoreDistribution: dist,
        par3avg, par4avg, par5avg,
        totalBeers, totalCigs,
      };
    });

    return {
      awards: { seasonLeaderboardWinner, mostImproved, mostGrossBirdies, bestSingleRound, ironMan, mostConsistent, mostBeers, mostCigarettes },
      playerStats,
    };
  };

  // silent = auto-run on tab open; no modals, just leaves the preview empty on failure
  const handleGeneratePreview = async ({ silent = false } = {}) => {
    const activeSeason = seasons.find(s => s.isActive);
    if (!activeSeason) {
      if (!silent) showAlert('No Active Season', 'No active season found.', 'error');
      return;
    }
    setRecapLoading(true);
    setRecapPreview(null);
    try {
      const roundsSnap = await getDocs(query(
        collection(db, 'rounds'),
        where('seasonId', '==', activeSeason.id),
        where('status', '==', 'approved')
      ));
      const rounds = roundsSnap.docs.map(d => d.data());
      if (rounds.length === 0) {
        if (!silent) showAlert('No Rounds Yet', 'No approved rounds for the active season yet.', 'info');
        setRecapLoading(false);
        return;
      }

      const usersSnap = await getDocs(collection(db, 'users'));
      const usersMap = {};
      usersSnap.docs.forEach(d => { usersMap[d.id] = d.data(); });

      // Season finale winner comes off the finale event document, not the rounds
      let seasonFinaleWinner = null;
      let finalePositions = {};           // { userId: position }
      let finaleParticipants = new Set(); // userIds who actually played the finale
      try {
        const finaleEventsSnap = await getDocs(query(
          collection(db, 'events'),
          where('seasonId', '==', activeSeason.id),
          where('type', '==', 'finale_18')
        ));
        if (!finaleEventsSnap.empty) {
          const finaleEventDoc = finaleEventsSnap.docs[0];
          const finaleEventId = finaleEventDoc.id;
          const finaleEvent = finaleEventDoc.data();
          if (finaleEvent.winnerRevealed && finaleEvent.winnerId) {
            seasonFinaleWinner = {
              userId: finaleEvent.winnerId,
              name: finaleEvent.winnerName || 'Unknown',
              netGross: finaleEvent.winnerNetGross || null,
            };
          }

          const nonParticipants = finaleEvent.nonParticipants || [];

          const finaleRoundsSnap = await getDocs(query(
            collection(db, 'finaleRounds'),
            where('eventId', '==', finaleEventId)
          ));

          // Only rounds with at least one hole completed count
          const finaleRounds = finaleRoundsSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(r => r.holesCompleted > 0);

          // Lowest net gross wins
          finaleRounds.sort((a, b) => (a.finaleNetGross || 0) - (b.finaleNetGross || 0));

          finaleRounds.forEach((r, index) => {
            finalePositions[r.userId] = index + 1;
            finaleParticipants.add(r.userId);
          });

          nonParticipants.forEach(uid => {
            finaleParticipants.delete(uid);
            delete finalePositions[uid];
          });
        }
      } catch (err) {
        console.error('Error fetching finale winner:', err);
      }

      const { awards, playerStats } = computeRecapData(rounds, usersMap, finalePositions, finaleParticipants);
      setRecapPreview({
        seasonId: activeSeason.id,
        seasonName: activeSeason.name,
        awards: { ...awards, seasonFinaleWinner },
        playerStats,
      });
    } catch (err) {
      console.error('Error generating recap:', err);
      if (!silent) showAlert('Error', 'Error generating recap. See console for details.', 'error');
    } finally {
      setRecapLoading(false);
    }
  };

  const handlePublishRecap = () => {
    if (!recapPreview) return;
    setConfirmModal({
      title: 'Publish Season Recap',
      message: `Publish the Season Recap for "${recapPreview.seasonName}"? All members will see it on their dashboard.`,
      emoji: '🚀',
      confirmLabel: 'Publish',
      confirmColor: '#10b981',
      onConfirm: async () => {
        setConfirmModal(null);
        setRecapPublishing(true);
        try {
          const data = {
            seasonId: recapPreview.seasonId,
            seasonName: recapPreview.seasonName,
            awards: recapPreview.awards,
            playerStats: recapPreview.playerStats,
            isPublished: true,
            publishedAt: serverTimestamp(),
          };
          await setDoc(doc(db, 'season_recaps', recapPreview.seasonId), data);
          setPublishedRecap(data);
          setRecapPreview(null);
        } catch (err) {
          console.error('Error publishing recap:', err);
          showAlert('Error', 'Error publishing recap.', 'error');
        } finally {
          setRecapPublishing(false);
        }
      },
    });
  };

  const handleUnpublishRecap = () => {
    if (!publishedRecap) return;
    setConfirmModal({
      title: 'Unpublish Recap',
      message: 'Unpublish this recap? It will no longer appear on member dashboards.',
      emoji: '⏸️',
      confirmLabel: 'Unpublish',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await setDoc(doc(db, 'season_recaps', publishedRecap.seasonId), { ...publishedRecap, isPublished: false }, { merge: true });
          setPublishedRecap(prev => ({ ...prev, isPublished: false }));
        } catch (err) {
          console.error('Error unpublishing recap:', err);
          showAlert('Error', 'Error unpublishing recap.', 'error');
        }
      },
    });
  };

  const handleDeleteRecap = () => {
    if (!publishedRecap) return;
    setConfirmModal({
      title: 'Delete Season Recap',
      message: `Permanently delete the recap for "${publishedRecap.seasonName}"? You can always regenerate it later.`,
      emoji: '🗑️',
      confirmLabel: 'Delete',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await deleteDoc(doc(db, 'season_recaps', publishedRecap.seasonId));
          setPublishedRecap(null);
          setRecapPreview(null);
        } catch (err) {
          console.error('Error deleting recap:', err);
          showAlert('Error', 'Error deleting recap.', 'error');
        }
      },
    });
  };

  // Load published recap when Season Recap tab is opened
  useEffect(() => {
    if (view !== 'recap') return;
    const activeSeason = seasons.find(s => s.isActive);
    if (!activeSeason) return;
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'season_recaps', activeSeason.id));
        if (snap.exists()) setPublishedRecap(snap.data());
        else setPublishedRecap(null);
      } catch (err) {
        console.error('Error loading recap status:', err);
      }
    };
    load();
  }, [view, seasons]);

  // Always compute a fresh preview from live rounds when the Season Recap tab
  // is opened, so the admin never looks at stale numbers from an earlier session.
  const recapAutoGenSeasonRef = useRef(null);
  useEffect(() => {
    if (view !== 'recap') { recapAutoGenSeasonRef.current = null; return; }
    const activeSeason = seasons.find(s => s.isActive);
    if (!activeSeason) return;
    if (recapAutoGenSeasonRef.current === activeSeason.id) return;
    recapAutoGenSeasonRef.current = activeSeason.id;
    handleGeneratePreview({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, seasons]);

  const getFinaleUserName = (userId) => allMembers.find(m => m.uid === userId)?.name || 'Unknown';

  const loadFinaleData = async () => {
    const activeSeasonObj = seasons.find(s => s.isActive);
    if (!activeSeasonObj) { setFinaleEvents([]); setFinaleRounds([]); return; }
    setFinaleLoading(true);
    try {
      const eventsSnap = await getDocs(query(
        collection(db, 'events'),
        where('seasonId', '==', activeSeasonObj.id),
        where('type', '==', 'finale_18')
      ));
      const finaleEventsData = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinaleEvents(finaleEventsData);

      for (const ev of finaleEventsData) {
        if (ev.nonParticipants) {
          setFinaleNonParticipants(ev.nonParticipants);
        }
      }

      let allRounds = [];
      for (const ev of finaleEventsData) {
        const roundsSnap = await getDocs(query(
          collection(db, 'rounds'),
          where('eventId', '==', ev.id)
        ));
        allRounds = allRounds.concat(roundsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      allRounds.sort((a, b) => (a.finaleNetGross ?? Infinity) - (b.finaleNetGross ?? Infinity));
      setFinaleRounds(allRounds);
    } catch (err) {
      console.error('Error loading finale data:', err);
    } finally {
      setFinaleLoading(false);
    }
  };

  // Load finale events + rounds when Finale tab opens
  useEffect(() => {
    if (view !== 'finale') return;
    loadFinaleData();
  }, [view, seasons]);

  const handleRevealWinner = (event, round) => {
    const winnerName = getFinaleUserName(round.userId);
    setConfirmModal({
      title: 'Reveal Season Finale Winner',
      message: `Reveal ${winnerName} as the Season Finale winner? This will show a celebratory popup to all members when they next open the app.`,
      emoji: '🏆',
      confirmLabel: 'Reveal Winner',
      confirmColor: '#d4af37',
      onConfirm: async () => {
        setConfirmModal(null);
        setRevealLoading(true);
        try {
          let photoURL = null;
          try {
            const userSnap = await getDoc(doc(db, 'users', round.userId));
            if (userSnap.exists()) photoURL = userSnap.data().photoURL || null;
          } catch (err) {
            console.error('Error fetching winner photo:', err);
          }

          await updateDoc(doc(db, 'events', event.id), {
            winnerRevealed: true,
            winnerId: round.userId,
            winnerName,
            winnerNetGross: round.finaleNetGross,
            winnerPhotoURL: photoURL,
            winnerRevealedAt: serverTimestamp(),
          });

          showAlert('Winner Revealed', 'Winner revealed! 🎉', 'success');
          await loadFinaleData();
        } catch (err) {
          console.error('Error revealing winner:', err);
          showAlert('Error', 'Error revealing winner. See console for details.', 'error');
        } finally {
          setRevealLoading(false);
        }
      },
    });
  };

  const handleUnrevealWinner = (event) => {
    setConfirmModal({
      title: 'Unpublish Finale Winner',
      message: `Unpublish ${event.winnerName} as the Season Finale winner? Members will no longer see the celebratory popup.`,
      emoji: '⏸️',
      confirmLabel: 'Unpublish',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await updateDoc(doc(db, 'events', event.id), { winnerRevealed: false });
          await loadFinaleData();
        } catch (err) {
          console.error('Error unpublishing winner:', err);
          showAlert('Error', 'Error unpublishing winner.', 'error');
        }
      },
    });
  };

  const handleToggleFinaleNonParticipant = (uid) => {
    setFinaleNonParticipants(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
    setNonParticipantsSaved(false);
  };

  const handleSaveNonParticipants = async () => {
    if (!finaleEvents[0]) return;
    setSavingNonParticipants(true);
    try {
      await updateDoc(doc(db, 'events', finaleEvents[0].id), {
        nonParticipants: finaleNonParticipants,
      });
      setNonParticipantsSaved(true);
    } catch (err) {
      console.error('Error saving non-participants:', err);
    } finally {
      setSavingNonParticipants(false);
    }
  };

  // Load global notification settings when the Notifications tab is opened
  useEffect(() => {
    if (view !== 'notifications') return;
    setNotificationSettingsLoading(true);
    getGlobalNotificationSettings()
      .then(setNotificationSettings)
      .finally(() => setNotificationSettingsLoading(false));
  }, [view]);

  const handleToggleNotificationSetting = async (key) => {
    const currentlyOn = notificationSettings[key] !== false;
    const updated = { ...notificationSettings, [key]: !currentlyOn };
    setNotificationSettings(updated);
    try {
      await setDoc(doc(db, 'settings', 'notificationConfig'), updated, { merge: true });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setNotificationSettings(notificationSettings);
      showAlert('Error', 'Could not save notification settings.', 'error');
    }
  };

  const handleClearTodayNotifications = () => {
    setConfirmModal({
      title: 'Clear Today\'s Notifications',
      message: 'Delete every notification created today, for every member? This cannot be undone.',
      emoji: '🗑️',
      confirmLabel: 'Delete',
      confirmColor: '#ef4444',
      onConfirm: async () => {
        setConfirmModal(null);
        setClearingNotifications(true);
        try {
          const startOfToday = new Date();
          startOfToday.setHours(0, 0, 0, 0);
          const q = query(collection(db, 'notifications'), where('timestamp', '>=', startOfToday));
          const snapshot = await getDocs(q);

          const docs = snapshot.docs;
          for (let i = 0; i < docs.length; i += 450) {
            const batch = writeBatch(db);
            docs.slice(i, i + 450).forEach((d) => batch.delete(d.ref));
            await batch.commit();
          }

          showAlert('Notifications Cleared', `Deleted ${docs.length} notification${docs.length === 1 ? '' : 's'} from today.`, 'success');
        } catch (error) {
          console.error('Error clearing today\'s notifications:', error);
          showAlert('Error', 'Error clearing notifications.', 'error');
        } finally {
          setClearingNotifications(false);
        }
      },
    });
  };

  if (loading && canManageMembers) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Loading...</h2>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>⚙️ Admin Panel</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.tabs}>
          {canManageSeasons && (
            <>
              <button
                onClick={() => setView('seasons')}
                style={view === 'seasons' ? styles.tabActive : styles.tab}
              >
                Seasons
              </button>
              <button
                onClick={() => setView('events')}
                style={view === 'events' ? styles.tabActive : styles.tab}
              >
                Events
              </button>
            </>
          )}
          {canManageMembers && (
            <button
              onClick={() => setView('members')}
              style={view === 'members' ? styles.tabActive : styles.tab}
            >
              Members {pendingMembers.length > 0 && `(${pendingMembers.length})`}
            </button>
          )}
          {userProfile.role === 'admin' && (
            <button
              onClick={() => setView('trophies')}
              style={view === 'trophies' ? styles.tabActive : styles.tab}
            >
              🏆 Trophies
            </button>
          )}
          {userProfile.role === 'admin' && (
            <button
              onClick={() => setView('recap')}
              style={view === 'recap' ? styles.tabActive : styles.tab}
            >
              🎬 Season Recap
            </button>
          )}
          {userProfile.role === 'admin' && (
            <button
              onClick={() => setView('finale')}
              style={view === 'finale' ? styles.tabActive : styles.tab}
            >
              🏆 Finale
            </button>
          )}
          {userProfile.role === 'admin' && (
            <button
              onClick={() => setView('notifications')}
              style={view === 'notifications' ? styles.tabActive : styles.tab}
            >
              🔔 Notifications
            </button>
          )}
        </div>

        <div style={styles.content}>
          {/* SEASONS TAB */}
          {view === 'seasons' && canManageSeasons && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Seasons</h3>
                <button 
                  onClick={() => setShowNewSeasonForm(!showNewSeasonForm)}
                  style={styles.addButton}
                >
                  {showNewSeasonForm ? 'Cancel' : '+ New Season'}
                </button>
              </div>

              {showNewSeasonForm && (
                <form onSubmit={handleCreateSeason} style={styles.form}>
                  <input
                    type="text"
                    placeholder="Season Name (e.g., 2025 Season)"
                    value={newSeasonName}
                    onChange={(e) => setNewSeasonName(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <input
                    type="date"
                    placeholder="Start Date"
                    value={newSeasonStartDate}
                    onChange={(e) => setNewSeasonStartDate(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <input
                    type="date"
                    placeholder="End Date"
                    value={newSeasonEndDate}
                    onChange={(e) => setNewSeasonEndDate(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <button type="submit" style={styles.submitButton}>
                    Create Season
                  </button>
                </form>
              )}

              <div style={styles.list}>
                {seasons.map(season => (
                  <div key={season.id} style={styles.listItem}>
                    <div>
                      <strong style={styles.itemName}>{season.name}</strong>
                      {season.isActive && <span style={styles.activeBadge}>ACTIVE</span>}
                      <div style={styles.itemDetails}>
                        {new Date(season.startDate).toLocaleDateString()} - {new Date(season.endDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={styles.itemActions}>
                      {!season.isActive && (
                        <button 
                          onClick={() => handleSetActiveSeason(season.id)}
                          style={styles.actionButton}
                        >
                          Set Active
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteSeason(season.id)}
                        style={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EVENTS TAB */}
          {view === 'events' && canManageSeasons && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Events</h3>
                <div style={styles.buttonGroup}>
                  <button 
                    onClick={() => {
                      setShowNewEventForm(!showNewEventForm);
                      setShowBulkEventForm(false);
                    }}
                    style={styles.addButton}
                  >
                    {showNewEventForm ? 'Cancel' : '+ New Event'}
                  </button>
                  <button 
                    onClick={() => {
                      setShowBulkEventForm(!showBulkEventForm);
                      setShowNewEventForm(false);
                    }}
                    style={styles.bulkButton}
                  >
                    {showBulkEventForm ? 'Cancel' : '+ Bulk Create'}
                  </button>
                </div>
              </div>

              {showNewEventForm && (
                <form onSubmit={handleCreateEvent} style={styles.form}>
                  <input
                    type="text"
                    placeholder="Event Name"
                    value={newEventName}
                    onChange={(e) => setNewEventName(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <input
                    type="date"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <select
                    value={newEventSeason}
                    onChange={(e) => setNewEventSeason(e.target.value)}
                    style={styles.select}
                    required
                  >
                    <option value="">Select Season</option>
                    {seasons.map(season => (
                      <option key={season.id} value={season.id}>{season.name}</option>
                    ))}
                  </select>
                  <select
                    value={newEventType}
                    onChange={(e) => setNewEventType(e.target.value)}
                    style={styles.select}
                  >
                    <option value="regular">Regular Event</option>
                    <option value="finale">Finale (Stroke Play)</option>
                    <option value="finale_18">🏆 Season Finale (18 holes)</option>
                  </select>

                  {newEventType === 'finale_18' && (
                    <div style={styles.finaleConfigBox}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Teebox Selection</label>
                        <select
                          value={finaleTeebox}
                          onChange={(e) => setFinaleTeebox(e.target.value)}
                          style={styles.select}
                        >
                          <option value="admin">Admin selects teebox for all players</option>
                          <option value="player">Players choose their own teebox</option>
                        </select>
                      </div>

                      {finaleTeebox === 'admin' && (
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Teebox Color</label>
                          <select
                            value={finaleAdminTeebox}
                            onChange={(e) => setFinaleAdminTeebox(e.target.value)}
                            style={styles.select}
                          >
                            {Object.entries(getBergvlietTeeboxes()).map(([key, tb]) => (
                              <option key={key} value={key}>{tb.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Bonus strokes for top finishers (season leaderboard)</label>
                        <div style={styles.bonusStrokesGrid}>
                          {[1, 2, 3, 4, 5].map(position => (
                            <label key={position} style={styles.bonusStrokeLabel}>
                              Pos {position}
                              <input
                                type="number"
                                value={bonusStrokes[position]}
                                onChange={(e) => setBonusStrokes({
                                  ...bonusStrokes,
                                  [position]: parseInt(e.target.value) || 0
                                })}
                                style={styles.bonusStrokeInput}
                                min="0"
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Minimum rounds to qualify for bonus strokes</label>
                        <input
                          type="number"
                          value={bonusMinRounds}
                          onChange={(e) => setBonusMinRounds(parseInt(e.target.value) || 0)}
                          style={styles.input}
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  <button type="submit" style={styles.submitButton}>
                    Create Event
                  </button>
                </form>
              )}

              {showBulkEventForm && (
                <form onSubmit={handleBulkCreateEvents} style={styles.form}>
                  <input
                    type="text"
                    placeholder="Base Name (e.g., 'Weekly Competition')"
                    value={bulkEventBaseName}
                    onChange={(e) => setBulkEventBaseName(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <input
                    type="number"
                    placeholder="Number of Events"
                    value={bulkEventCount}
                    onChange={(e) => setBulkEventCount(parseInt(e.target.value))}
                    style={styles.input}
                    min="1"
                    max="52"
                    required
                  />
                  <input
                    type="date"
                    placeholder="Start Date"
                    value={bulkEventStartDate}
                    onChange={(e) => setBulkEventStartDate(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <select
                    value={bulkEventSeason}
                    onChange={(e) => setBulkEventSeason(e.target.value)}
                    style={styles.select}
                    required
                  >
                    <option value="">Select Season</option>
                    {seasons.map(season => (
                      <option key={season.id} value={season.id}>{season.name}</option>
                    ))}
                  </select>
                  <p style={styles.helperText}>
                    Creates events weekly starting from the selected date
                  </p>
                  <button type="submit" style={styles.submitButton}>
                    Create {bulkEventCount} Events
                  </button>
                </form>
              )}

              <div style={styles.list}>
                {events.map(event => (
                  <div key={event.id} style={styles.listItem}>
                    <div>
                      <strong style={styles.itemName}>{event.name}</strong>
                      {event.type === 'finale' && <span style={styles.finaleBadge}>FINALE</span>}
                      <div style={styles.itemDetails}>
                        {new Date(event.date).toLocaleDateString()} • {getSeasonName(event.seasonId)}
                      </div>
                    </div>
                    <div style={styles.itemActions}>
                      <button 
                        onClick={() => handleDeleteEvent(event.id)}
                        style={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MEMBERS TAB */}
          {view === 'members' && canManageMembers && (
            <div>
              {pendingMembers.length > 0 && (
                <>
                  <h3 style={styles.sectionTitle}>Pending Approvals</h3>
                  <div style={styles.list}>
                    {pendingMembers.map(member => (
                      <div key={member.uid} style={styles.listItem}>
                        <div>
                          <strong style={styles.itemName}>{member.name}</strong>
                          <div style={styles.itemDetails}>
                            {member.email} • Handicap: {member.handicap}
                          </div>
                        </div>
                        <div style={styles.itemActions}>
                          <button 
                            onClick={() => handleApproveMember(member.uid)}
                            style={styles.approveButton}
                          >
                            Approve
                          </button>
                          <button 
                            onClick={() => handleDenyMember(member.uid)}
                            style={styles.denyButton}
                          >
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 style={styles.sectionTitle}>All Members</h3>
              <div style={styles.list}>
                {allMembers.filter(m => m.status === 'approved').map(member => (
                  <div key={member.uid} style={styles.listItem}>
                    <div>
                      <strong style={styles.itemName}>{member.name}</strong>
                      {member.role === 'admin' && <span style={styles.adminBadge}>ADMIN</span>}
                      {member.role === 'moderator' && <span style={styles.modBadge}>{member.officerTitle || 'MODERATOR'}</span>}
                      {member.role === 'officer' && <span style={styles.officerBadge}>{member.officerTitle}</span>}
                      <div style={styles.itemDetails}>
                        {member.email} • Handicap: {member.handicap}
                      </div>
                    </div>
                    <div style={styles.itemActions}>
                      {userProfile.role === 'admin' && member.role !== 'admin' && (
                        <>
                          {member.role === 'officer' ? (
                            <button 
                              onClick={() => handleDemoteFromOfficer(member.uid)}
                              style={styles.actionButton}
                            >
                              Remove Officer
                            </button>
                          ) : (
                            <button 
                              onClick={() => handlePromoteToOfficer(member.uid, member.role)}
                              style={styles.actionButton}
                            >
                              Make Officer
                            </button>
                          )}
                        </>
                      )}
                      {(userProfile.role === 'admin' || 
                        (userProfile.role === 'moderator' && member.role === 'member')) && 
                        member.uid !== currentUserId && (
                        <button 
                          onClick={() => handleRevokeMembership(member.uid)}
                          style={styles.revokeButton}
                        >
                          Revoke Membership
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TROPHIES TAB */}
{view === 'trophies' && userProfile.role === 'admin' && (
  <div>
    <div style={styles.sectionHeader}>
      <h3 style={styles.sectionTitle}>Award Special Trophies</h3>
      <button 
        onClick={() => setShowTrophyForm(!showTrophyForm)}
        style={styles.addButton}
      >
        {showTrophyForm ? 'Cancel' : '+ Award Trophy'}
      </button>
    </div>

    {showTrophyForm && (
      <form onSubmit={handleAwardTrophy} style={styles.trophyForm}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Select Player</label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={styles.select}
            required
          >
            <option value="">Choose a player...</option>
            {allMembers.filter(m => m.status === 'approved').map(member => (
              <option key={member.uid} value={member.uid}>{member.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Trophy Type</label>
          <select
            value={trophyType}
            onChange={(e) => setTrophyType(e.target.value)}
            style={styles.select}
            required
          >
            <option value="season_champion">🏆 Season Champion</option>
            <option value="finale_winner">⭐ Finale Winner</option>
            <option value="hall_of_fame">👑 Hall of Fame</option>
            <option value="most_improved">🔥 Most Improved</option>
            <option value="lifetime_achievement">💎 Lifetime Achievement</option>
            <option value="custom">🎯 Custom Trophy</option>
          </select>
        </div>

        {trophyType === 'custom' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label}>Trophy Name</label>
              <input
                type="text"
                placeholder="e.g., Best Sportsmanship"
                value={customTrophyName}
                onChange={(e) => setCustomTrophyName(e.target.value)}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Trophy Icon (Emoji)</label>
              <input
                type="text"
                placeholder="🏆"
                value={customTrophyIcon}
                onChange={(e) => setCustomTrophyIcon(e.target.value)}
                style={styles.input}
                maxLength="2"
                required
              />
            </div>
          </>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Year</label>
          <input
            type="number"
            value={trophyYear}
            onChange={(e) => setTrophyYear(e.target.value)}
            style={styles.input}
            min="2020"
            max="2030"
            required
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Note (Optional)</label>
          <textarea
            placeholder="e.g., Won with record-breaking 35 average points"
            value={trophyNote}
            onChange={(e) => setTrophyNote(e.target.value)}
            style={styles.textarea}
            rows="3"
          />
        </div>

        <button type="submit" style={styles.submitButton}>
          🏆 Award Trophy
        </button>
      </form>
    )}

    {/* Existing Trophies List */}
    {existingTrophies.length > 0 && (
      <div style={{marginBottom: '25px'}}>
        <h3 style={{...styles.sectionTitle, marginBottom: '15px'}}>
          Awarded Trophies ({existingTrophies.length})
        </h3>
        <div style={styles.list}>
          {existingTrophies.map(trophy => (
            <div key={trophy.id} style={styles.listItem}>
              <div>
                <strong style={styles.itemName}>
                  {trophy.icon} {trophy.name}
                </strong>
                <div style={styles.itemDetails}>
                  👤 {allUsersMap[trophy.userId]?.name || 'Unknown'} · 
                  📅 {trophy.year}
                  {trophy.note && ` · "${trophy.note}"`}
                </div>
              </div>
              <button
                onClick={() => handleDeleteTrophy(trophy.id)}
                style={styles.deleteButton}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    <div style={styles.infoBox}>
      <h4 style={styles.infoTitle}>ℹ️ About Special Trophies</h4>
      <p style={styles.infoText}>
        Award trophies to recognize past champions and special achievements.
        These trophies will appear on the player's profile and in the Hall of Fame.
      </p>
      <ul style={styles.infoList}>
        <li>🏆 <strong>Season Champion:</strong> For past season winners</li>
        <li>⭐ <strong>Finale Winner:</strong> Won the season finale</li>
        <li>👑 <strong>Hall of Fame:</strong> Legendary status</li>
        <li>🔥 <strong>Most Improved:</strong> Best improvement over a season</li>
        <li>💎 <strong>Lifetime Achievement:</strong> Long-term excellence</li>
        <li>🎯 <strong>Custom:</strong> Create your own trophy</li>
      </ul>
    </div>
  </div>
)}

          {/* SEASON RECAP TAB */}
          {view === 'recap' && userProfile.role === 'admin' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>🎬 Season Recap</h3>
              </div>

              {/* Published status banner */}
              {publishedRecap && (
                <div style={{
                  backgroundColor: publishedRecap.isPublished ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                  border: `1px solid ${publishedRecap.isPublished ? '#10b981' : '#374151'}`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: publishedRecap.isPublished ? '#10b981' : '#9ca3af' }}>
                      {publishedRecap.isPublished ? '✅ Recap Published' : '⏸ Recap Unpublished'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                      {publishedRecap.seasonName}
                      {publishedRecap.isPublished ? ' — visible to all members on their dashboard' : ' — not visible to members'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={publishedRecap.isPublished ? handleUnpublishRecap : handlePublishRecap}
                      style={{
                        padding: '8px 18px',
                        backgroundColor: publishedRecap.isPublished ? '#374151' : '#10b981',
                        color: publishedRecap.isPublished ? '#9ca3af' : '#0f0f0f',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                      }}
                    >
                      {publishedRecap.isPublished ? 'Unpublish' : 'Re-publish'}
                    </button>
                    <button
                      onClick={handleDeleteRecap}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: 'transparent',
                        color: '#ef4444',
                        border: '1px solid #ef444430',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              )}

              {/* Generate preview */}
              {!recapPreview && (
                <div style={styles.infoBox}>
                  <h4 style={styles.infoTitle}>How It Works</h4>
                  <p style={styles.infoText}>
                    Generate a preview of the Season Recap based on all approved rounds for the active season.
                    Review the computed awards, then publish to make it visible to all members.
                  </p>
                  <ul style={styles.infoList}>
                    <li>🏆 <strong>Season Leaderboard Winner</strong> — highest average stableford points</li>
                    <li>⭐ <strong>Season Finale Winner</strong> — winner of the revealed 18-hole finale</li>
                    <li>📈 <strong>Most Improved</strong> — biggest rise from first half to second half of the season (10+ rounds)</li>
                    <li>🐦 <strong>Most Birdies</strong> — most gross birdies across the season</li>
                    <li>🎯 <strong>Best Single Round</strong> — highest stableford score in one round</li>
                    <li>💪 <strong>Iron Man</strong> — most rounds played (ties share the award)</li>
                    <li>🎪 <strong>Most Consistent</strong> — most rounds scored at 16+ stableford points</li>
                    <li>🍺 <strong>Most Beers</strong> — most post-round beers logged</li>
                    <li>🚬 <strong>Most Cigarettes</strong> — most cigarettes smoked/borrowed</li>
                  </ul>
                  <button
                    onClick={() => handleGeneratePreview()}
                    disabled={recapLoading || !seasons.find(s => s.isActive)}
                    style={{
                      marginTop: '16px',
                      padding: '12px 28px',
                      backgroundColor: recapLoading ? '#374151' : '#d4af37',
                      color: recapLoading ? '#9ca3af' : '#0f0f0f',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: recapLoading ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '700',
                    }}
                  >
                    {recapLoading ? 'Generating…' : !seasons.find(s => s.isActive) ? 'No Active Season' : '⚡ Generate Preview'}
                  </button>
                </div>
              )}

              {/* Preview */}
              {recapPreview && (
                <div>
                  <div style={{
                    backgroundColor: '#0f0f0f',
                    border: '1px solid #2d2d2d',
                    borderRadius: '10px',
                    padding: '20px',
                    marginBottom: '20px',
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#d4af37', marginBottom: '16px' }}>
                      Preview — {recapPreview.seasonName}
                    </div>
                    {[
                      { label: '🏆 Season Leaderboard Winner', data: recapPreview.awards.seasonLeaderboardWinner, stat: d => `${d.totalPoints} pts` },
                      { label: '⭐ Season Finale Winner', data: recapPreview.awards.seasonFinaleWinner,  stat: d => d.netGross ? `${d.netGross} net gross` : 'Finale winner' },
                      { label: '📈 Most Improved',     data: recapPreview.awards.mostImproved,     stat: d => d.firstAvg != null ? `+${d.improvement} pts — ${d.firstAvg} → ${d.secondAvg} avg` : `+${d.improvement} pts improvement` },
                      { label: '🐦 Most Birdies',      data: recapPreview.awards.mostGrossBirdies,  stat: d => d.perRound != null ? `${d.count} birdies — ${d.perRound}/round over ${d.roundsPlayed} rounds` : `${d.count} birdies` },
                      { label: '🎯 Best Single Round', data: recapPreview.awards.bestSingleRound,  stat: d => `${d.points} pts${d.eventName ? ` — ${d.eventName}` : ''}` },
                      { label: '💪 Iron Man',          data: recapPreview.awards.ironMan,          stat: d => d.isMultiple ? `${d.roundsPlayed} rounds each` : `${d.roundsPlayed} rounds` },
                      { label: '🎪 Most Consistent',   data: recapPreview.awards.mostConsistent,   stat: d => d.count != null ? `${d.count} of ${d.roundsPlayed} rounds at 16+ pts (${d.pct}%) — ${d.avg} avg pts` : `stdDev ${d.stdDev} pts` },
                      { label: '🍺 Most Beers',        data: recapPreview.awards.mostBeers,        stat: d => `${d.count} beers` },
                      { label: '🚬 Most Cigarettes',   data: recapPreview.awards.mostCigarettes,   stat: d => `${d.count} cigarettes` },
                    ].map(({ label, data, stat }) => (
                      <div key={label} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 0',
                        borderBottom: '1px solid #1a1a1a',
                        gap: '12px',
                        flexWrap: 'wrap',
                      }}>
                        <div style={{ fontSize: '13px', color: '#9ca3af', minWidth: '180px' }}>{label}</div>
                        {data ? (
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>{data.name}</span>
                            <span style={{ fontSize: '13px', color: '#d4af37' }}>{stat(data)}</span>
                          </div>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#4b5563', fontStyle: 'italic' }}>No data</span>
                        )}
                      </div>
                    ))}
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '14px' }}>
                      {Object.keys(recapPreview.playerStats).length} players · {
                        Object.values(recapPreview.playerStats).reduce((s, p) => s + p.roundsPlayed, 0)
                      } total rounds
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={handlePublishRecap}
                      disabled={recapPublishing}
                      style={{
                        padding: '12px 28px',
                        backgroundColor: recapPublishing ? '#374151' : '#10b981',
                        color: recapPublishing ? '#9ca3af' : '#0f0f0f',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: recapPublishing ? 'not-allowed' : 'pointer',
                        fontSize: '15px',
                        fontWeight: '700',
                      }}
                    >
                      {recapPublishing ? 'Publishing…' : '🚀 Publish Recap'}
                    </button>
                    <button
                      onClick={() => setRecapPreview(null)}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        color: '#9ca3af',
                        border: '1px solid #2d2d2d',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleGeneratePreview()}
                      disabled={recapLoading}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        color: '#d4af37',
                        border: '1px solid #d4af37',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                      }}
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FINALE TAB */}
          {view === 'finale' && userProfile.role === 'admin' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>🏆 Season Finale</h3>
              </div>

              {finaleLoading ? (
                <div style={{ fontSize: '14px', color: '#9ca3af', padding: '20px 0' }}>Loading…</div>
              ) : finaleEvents.length === 0 ? (
                <div style={{ fontSize: '14px', color: '#9ca3af', padding: '20px 0' }}>
                  No finale events found for the active season.
                </div>
              ) : (
                finaleEvents.map(event => {
                  const eventRounds = finaleRounds.filter(r => r.eventId === event.id);
                  const approvedRounds = eventRounds.filter(r => r.status === 'approved');
                  const pendingRounds = eventRounds.filter(r => r.status === 'pending');
                  const disputedRounds = eventRounds.filter(r => r.status === 'disputed');
                  const topApprovedRound = approvedRounds[0];
                  const top5 = approvedRounds.slice(0, 5);

                  return (
                    <div key={event.id} style={{
                      backgroundColor: '#0f0f0f',
                      border: '1px solid #2d2d2d',
                      borderRadius: '10px',
                      padding: '20px',
                      marginBottom: '20px',
                    }}>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>{event.name}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          {new Date(event.date).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Submission status pills */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                        {[
                          { label: 'Total', value: eventRounds.length, color: '#9ca3af' },
                          { label: 'Approved', value: approvedRounds.length, color: '#10b981' },
                          { label: 'Pending', value: pendingRounds.length, color: '#f59e0b' },
                          { label: 'Disputed', value: disputedRounds.length, color: '#ef4444' },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{
                            backgroundColor: '#1e1e1e',
                            border: '1px solid #2d2d2d',
                            borderRadius: '10px',
                            padding: '14px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            minWidth: '90px',
                          }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color }}>
                              {label}
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '700', color }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Participant management */}
                      <div style={{
                        backgroundColor: '#0f0f0f',
                        border: '1px solid #2d2d2d',
                        borderRadius: '10px',
                        padding: '20px',
                        marginBottom: '20px',
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                          👥 Participant Management
                        </div>
                        <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                          Mark players who are NOT participating in the finale. They will be hidden at the bottom of the leaderboard.
                        </div>

                        {allMembers.filter(m => m.status === 'approved').map(member => {
                          const isNonParticipant = finaleNonParticipants.includes(member.uid);
                          return (
                            <div key={member.uid} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 0',
                              borderBottom: '1px solid #1a1a1a',
                            }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isNonParticipant ? '#6b7280' : '#ffffff',
                              }}>
                                {member.name}
                                {isNonParticipant && (
                                  <span style={{ fontSize: '11px', color: '#ef4444', marginLeft: '8px' }}>
                                    (Not participating)
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleToggleFinaleNonParticipant(member.uid)}
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  border: isNonParticipant ? '1px solid #ef4444' : '1px solid #10b981',
                                  backgroundColor: isNonParticipant ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                                  color: isNonParticipant ? '#ef4444' : '#10b981',
                                }}
                              >
                                {isNonParticipant ? '✗ Not Playing' : '✓ Participating'}
                              </button>
                            </div>
                          );
                        })}

                        <button
                          onClick={handleSaveNonParticipants}
                          disabled={savingNonParticipants}
                          style={{
                            marginTop: '16px',
                            padding: '10px 24px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '700',
                            border: 'none',
                            cursor: savingNonParticipants ? 'default' : 'pointer',
                            backgroundColor: nonParticipantsSaved ? '#10b981' : '#d4af37',
                            color: '#0f0f0f',
                          }}
                        >
                          {nonParticipantsSaved ? '✅ Saved!' : '💾 Save Participant List'}
                        </button>
                      </div>

                      {/* Leaderboard preview */}
                      {top5.length === 0 ? (
                        <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic', margin: '0 0 20px' }}>
                          No approved finale rounds yet.
                        </div>
                      ) : (
                        <div style={{ marginBottom: '20px', overflowX: 'auto' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 90px 60px 60px 60px', gap: '8px', padding: '8px 4px', borderBottom: '1px solid #2d2d2d', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', minWidth: '480px' }}>
                            <div>#</div><div>Player</div><div>Teebox</div><div>Gross</div><div>Bonus</div><div>Net</div>
                          </div>
                          {top5.map((round, idx) => (
                            <div key={round.id} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 90px 60px 60px 60px', gap: '8px', padding: '10px 4px', borderBottom: '1px solid #1a1a1a', alignItems: 'center', fontSize: '13px', minWidth: '480px' }}>
                              <div style={{ color: '#9ca3af' }}>{idx + 1}</div>
                              <div style={{ color: '#ffffff', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {getFinaleUserName(round.userId)}
                                {round.status !== 'approved' && (
                                  <span style={{
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    color: round.status === 'pending' ? '#f59e0b' : '#ef4444',
                                    backgroundColor: round.status === 'pending' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                                  }}>
                                    {round.status === 'pending' ? 'PENDING' : 'DISPUTED'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 10px',
                                  borderRadius: '10px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  color: '#0f0f0f',
                                  backgroundColor: TEEBOX_COLORS[round.teeboxColor] || '#6b7280',
                                }}>
                                  {round.teeboxColor}
                                </span>
                              </div>
                              <div style={{ color: '#9ca3af' }}>{round.totalGross}</div>
                              <div style={{ color: '#9ca3af' }}>{round.finaleBonus || 0}</div>
                              <div style={{ color: '#d4af37', fontWeight: '700' }}>{round.finaleNetGross}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reveal winner controls */}
                      {event.winnerRevealed ? (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap',
                          backgroundColor: 'rgba(16,185,129,0.1)',
                          border: '1px solid #10b981',
                          borderRadius: '8px',
                          padding: '14px 18px',
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
                            ✅ Winner Already Revealed — {event.winnerName}
                          </div>
                          <button
                            onClick={() => handleUnrevealWinner(event)}
                            style={{
                              padding: '8px 18px',
                              backgroundColor: '#374151',
                              color: '#9ca3af',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                            }}
                          >
                            Unpublish
                          </button>
                        </div>
                      ) : topApprovedRound ? (
                        <button
                          onClick={() => handleRevealWinner(event, topApprovedRound)}
                          disabled={revealLoading}
                          style={{
                            backgroundColor: revealLoading ? '#374151' : '#d4af37',
                            color: revealLoading ? '#9ca3af' : '#0f0f0f',
                            fontWeight: 700,
                            padding: '14px 28px',
                            borderRadius: '10px',
                            width: '100%',
                            border: 'none',
                            cursor: revealLoading ? 'not-allowed' : 'pointer',
                            fontSize: '15px',
                          }}
                        >
                          {revealLoading ? 'Revealing…' : '🏆 Reveal Winner'}
                        </button>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {view === 'notifications' && userProfile.role === 'admin' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>🔔 Notifications</h3>
              </div>

              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px', lineHeight: 1.5 }}>
                These switches control notifications for every member at once — handy for turning off
                the noise while testing. A member's own preference (Settings → Notifications) still applies
                on top of this.
              </div>

              {notificationSettingsLoading ? (
                <div style={{ fontSize: '14px', color: '#9ca3af', padding: '20px 0' }}>Loading…</div>
              ) : (
                <div style={{
                  backgroundColor: '#0f0f0f',
                  border: '1px solid #2d2d2d',
                  borderRadius: '10px',
                  padding: '8px 20px',
                  marginBottom: '24px',
                }}>
                  {NOTIFICATION_TYPES.map(({ key, label }, i) => {
                    const isOn = notificationSettings[key] !== false;
                    const isLast = i === NOTIFICATION_TYPES.length - 1;
                    return (
                      <div
                        key={key}
                        onClick={() => handleToggleNotificationSetting(key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 0',
                          borderBottom: isLast ? 'none' : '1px solid #2d2d2d',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#ffffff', fontWeight: '500' }}>{label}</span>
                        <div style={{
                          width: '44px',
                          height: '24px',
                          borderRadius: '12px',
                          position: 'relative',
                          transition: 'background-color 0.2s ease',
                          backgroundColor: isOn ? '#d4af37' : '#374151',
                          flexShrink: 0,
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            transition: 'transform 0.2s ease',
                            transform: isOn ? 'translateX(22px)' : 'translateX(2px)',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Cleanup</h3>
              </div>
              <div style={{
                backgroundColor: '#0f0f0f',
                border: '1px solid #2d2d2d',
                borderRadius: '10px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
              }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', lineHeight: 1.5, maxWidth: '360px' }}>
                  Deletes every notification created today, for every member — useful after a testing session
                  so members don't see a pile of stale unread notifications.
                </div>
                <button
                  onClick={handleClearTodayNotifications}
                  disabled={clearingNotifications}
                  style={{
                    backgroundColor: clearingNotifications ? '#374151' : '#ef4444',
                    color: '#ffffff',
                    fontWeight: 600,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: clearingNotifications ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {clearingNotifications ? 'Clearing…' : '🗑️ Clear Today\'s Notifications'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>

    {/* Custom branded confirm modal */}
    {confirmModal && (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px',
      }}>
        <div style={{
          backgroundColor: '#1e1e1e',
          border: '1px solid #2d2d2d',
          borderRadius: '16px',
          padding: '40px 32px 32px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '52px', marginBottom: '16px', lineHeight: 1 }}>
            {confirmModal.emoji}
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '12px',
            letterSpacing: '-0.3px',
          }}>
            {confirmModal.title}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#9ca3af',
            lineHeight: '1.65',
            marginBottom: '32px',
          }}>
            {confirmModal.message}
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => setConfirmModal(null)}
              style={{
                padding: '11px 24px',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                border: '1px solid #374151',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmModal.onConfirm}
              style={{
                padding: '11px 28px',
                backgroundColor: confirmModal.confirmColor,
                color: '#0f0f0f',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '0.2px',
              }}
            >
              {confirmModal.confirmLabel}
            </button>
          </div>
          <div style={{
            marginTop: '20px',
            fontSize: '11px',
            color: '#374151',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}>
            KWAC Golf Club
          </div>
        </div>
      </div>
    )}

    {/* Custom branded alert modal (replaces alert()) */}
    {alertModal && (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px',
      }}>
        <div style={{
          backgroundColor: '#1e1e1e',
          border: '1px solid #2d2d2d',
          borderRadius: '16px',
          padding: '40px 32px 32px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '52px', marginBottom: '16px', lineHeight: 1 }}>
            {alertModal.emoji}
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '12px',
            letterSpacing: '-0.3px',
          }}>
            {alertModal.title}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#9ca3af',
            lineHeight: '1.65',
            marginBottom: '32px',
          }}>
            {alertModal.message}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => setAlertModal(null)}
              style={{
                padding: '11px 32px',
                backgroundColor: alertModal.type === 'error' ? '#ef4444' : alertModal.type === 'success' ? '#10b981' : '#d4af37',
                color: '#0f0f0f',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '0.2px',
              }}
            >
              OK
            </button>
          </div>
          <div style={{
            marginTop: '20px',
            fontSize: '11px',
            color: '#374151',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}>
            KWAC Golf Club
          </div>
        </div>
      </div>
    )}

    {/* Custom branded prompt modal (replaces prompt()) */}
    {promptModal && (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px',
      }}>
        <div style={{
          backgroundColor: '#1e1e1e',
          border: '1px solid #2d2d2d',
          borderRadius: '16px',
          padding: '40px 32px 32px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '52px', marginBottom: '16px', lineHeight: 1 }}>
            {promptModal.emoji}
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '12px',
            letterSpacing: '-0.3px',
          }}>
            {promptModal.title}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#9ca3af',
            lineHeight: '1.65',
            marginBottom: '20px',
          }}>
            {promptModal.message}
          </div>
          <input
            type="text"
            autoFocus
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder={promptModal.placeholder}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #2d2d2d',
              borderRadius: '8px',
              outline: 'none',
              backgroundColor: '#0f0f0f',
              color: '#ffffff',
              fontWeight: '500',
              marginBottom: '24px',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => setPromptModal(null)}
              style={{
                padding: '11px 24px',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                border: '1px solid #374151',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => promptModal.onConfirm(promptValue)}
              style={{
                padding: '11px 28px',
                backgroundColor: '#d4af37',
                color: '#0f0f0f',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '0.2px',
              }}
            >
              {promptModal.confirmLabel}
            </button>
          </div>
          <div style={{
            marginTop: '20px',
            fontSize: '11px',
            color: '#374151',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}>
            KWAC Golf Club
          </div>
        </div>
      </div>
    )}
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #2d2d2d',
    padding: '0 30px',
    gap: '5px',
    flexShrink: 0,
    overflowX: 'auto'
  },
  tab: {
    padding: '15px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease'
  },
  tabActive: {
    padding: '15px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid #d4af37',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#d4af37',
    whiteSpace: 'nowrap'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '30px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  bulkButton: {
    padding: '10px 20px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  form: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    border: '1px solid #2d2d2d'
  },
  trophyForm: {
    backgroundColor: '#1a1a1a',
    padding: '25px',
    borderRadius: '12px',
    marginBottom: '25px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    border: '2px solid #d4af37'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  finaleConfigBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #d4af37',
    backgroundColor: 'rgba(212,175,55,0.05)'
  },
  bonusStrokesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '10px'
  },
  bonusStrokeLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    minWidth: 0
  },
  bonusStrokeInput: {
    padding: '12px 8px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  select: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  textarea: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  helperText: {
    fontSize: '13px',
    color: '#6b7280',
    fontStyle: 'italic',
    margin: 0,
    fontWeight: '400'
  },
  submitButton: {
    padding: '12px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: '#0f0f0f',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  itemName: {
    fontSize: '16px',
    color: '#ffffff',
    marginRight: '10px',
    fontWeight: '600'
  },
  itemDetails: {
    fontSize: '13px',
    color: '#9ca3af',
    marginTop: '5px',
    fontWeight: '400'
  },
  itemActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  activeBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    marginLeft: '10px'
  },
  finaleBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#f59e0b',
    color: '#0f0f0f',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    marginLeft: '10px'
  },
  adminBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    marginLeft: '10px'
  },
  modBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    marginLeft: '10px'
  },
  officerBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '700',
    marginLeft: '10px'
  },
  actionButton: {
    padding: '8px 16px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  approveButton: {
    padding: '8px 16px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  denyButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  revokeButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  deleteButton: {
    padding: '8px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  infoBox: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #10b981',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '25px'
  },
  infoTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#10b981',
    margin: '0 0 10px 0',
    letterSpacing: '-0.01em'
  },
  infoText: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '0 0 15px 0',
    lineHeight: '1.5',
    fontWeight: '400'
  },
  infoList: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: 0,
    paddingLeft: '20px',
    lineHeight: '1.8',
    fontWeight: '400'
  }
};

export default AdminPanel;